import { useEffect } from "react";
import { createPortal } from "react-dom";

import { useShellChrome } from "./shell-chrome";

export interface ShellSubrailItem {
  key: string;
  label: string;
  /**
   * Optional attention count. Rendered as a quiet chip; the caller decides what deserves a
   * number (amber and red live upstream in the row itself - the subrail never colors).
   */
  count?: number;
  /**
   * Accessible phrasing for the count (e.g. "1 waiting on you"). Screen readers hear this
   * instead of the bare chip number, and the phone select keeps it in the option label so
   * the count survives the narrow presentation.
   */
  countLabel?: string;
  /**
   * Optional group heading. Consecutive items sharing a group render under one heading in
   * the vertical rail and the subnav strip, and under one optgroup in the phone select.
   * Flat lists simply omit it (Setup, Patrols); grouped indexes name it (Help sections).
   */
  group?: string;
  /**
   * Renders the item but refuses selection, so an index can stay honest about entries that
   * exist in the IA but are not ready (Help's planned articles). Pair with `tag`.
   */
  disabled?: boolean;
  /** Quiet suffix chip naming why an item is present but inert, e.g. "planned". */
  tag?: string;
  /** Optional tooltip (title attribute) for the list button. */
  hint?: string;
}

/** Consecutive runs of items sharing a group, preserving item order. */
function groupRuns(
  items: readonly ShellSubrailItem[],
): { group: string | undefined; items: ShellSubrailItem[] }[] {
  const runs: { group: string | undefined; items: ShellSubrailItem[] }[] = [];
  for (const item of items) {
    const last = runs[runs.length - 1];
    if (last !== undefined && last.group === item.group) {
      last.items.push(item);
    } else {
      runs.push({ group: item.group, items: [item] });
    }
  }
  return runs;
}

/** Option text carries what the visual chips carry: the count's meaning, then any tag. */
function optionLabel(item: ShellSubrailItem): string {
  let text = item.label;
  const countText = item.countLabel ?? (item.count !== undefined ? String(item.count) : undefined);
  if (countText !== undefined) text += ` (${countText})`;
  if (item.tag !== undefined) text += ` (${item.tag})`;
  return text;
}

/**
 * The optional secondary rail of the shell v3 contract (product lock 2026-08-15, lock 3).
 *
 * One component, three presentations, all CSS-owned so every route inherits the same
 * collapse order: a 216px vertical rail beside the stage at 1200px and up, a sticky
 * horizontal subnav strip above the stage between 768 and 1199px, and a select at 767px
 * and below. Both the list and the select render; breakpoints choose which one shows, so
 * no JS viewport state is involved in the swap.
 *
 * Inside the app shell this portals into the shell's subrail grid cell and registers so
 * the column exists only while a subrail is mounted. Without a shell (unit tests, embeds)
 * it renders in place.
 */
export function ShellSubrail({
  eyebrow,
  items,
  activeKey,
  onSelect,
}: {
  /** Small uppercase context line, e.g. "Workspace setup". Also names the nav landmark. */
  eyebrow?: string;
  items: readonly ShellSubrailItem[];
  activeKey?: string;
  onSelect?: (key: string) => void;
}) {
  const chrome = useShellChrome();
  const registerSubrail = chrome?.registerSubrail;

  useEffect(
    () => (registerSubrail !== undefined ? registerSubrail() : undefined),
    [registerSubrail],
  );

  const label = eyebrow ?? "Page sections";
  const runs = groupRuns(items);
  const renderItem = (item: ShellSubrailItem) => (
    <li key={item.key} className="subrail__item">
      <button
        type="button"
        className={
          item.key === activeKey ? "subrail__link subrail__link--active" : "subrail__link"
        }
        aria-current={item.key === activeKey ? "true" : undefined}
        disabled={item.disabled}
        title={item.hint}
        onClick={() => {
          if (item.disabled !== true) onSelect?.(item.key);
        }}
      >
        <span className="subrail__label">{item.label}</span>
        {item.count !== undefined ? (
          <span
            className="subrail__count"
            aria-hidden={item.countLabel !== undefined ? "true" : undefined}
          >
            {item.count}
          </span>
        ) : null}
        {item.countLabel !== undefined ? (
          <span className="subrail__count-label">{item.countLabel}</span>
        ) : null}
        {item.tag !== undefined ? <span className="subrail__tag">{item.tag}</span> : null}
      </button>
    </li>
  );
  const renderOption = (item: ShellSubrailItem) => (
    <option key={item.key} value={item.key} disabled={item.disabled}>
      {optionLabel(item)}
    </option>
  );
  const rail = (
    <nav className="subrail" aria-label={label}>
      {eyebrow !== undefined ? <p className="subrail__eyebrow">{eyebrow}</p> : null}
      <ul className="subrail__list">
        {runs.map((run, index) => [
          run.group !== undefined ? (
            <li key={`group-${run.group}-${index}`} className="subrail__group">
              {run.group}
            </li>
          ) : null,
          run.items.map(renderItem),
        ])}
      </ul>
      <label className="subrail__select">
        <span className="subrail__select-label">{label}</span>
        <select
          aria-label={label}
          value={activeKey ?? items.find((item) => item.disabled !== true)?.key ?? ""}
          onChange={(event) => onSelect?.(event.target.value)}
        >
          {runs.map((run, index) =>
            run.group !== undefined ? (
              <optgroup key={`group-${run.group}-${index}`} label={run.group}>
                {run.items.map(renderOption)}
              </optgroup>
            ) : (
              run.items.map(renderOption)
            ),
          )}
        </select>
      </label>
    </nav>
  );

  if (chrome === null) {
    return rail;
  }
  if (chrome.subrailHost === null) {
    // The host cell mounts in the same commit as the shell body; the portal lands on the
    // re-render its ref triggers. Rendering inline here instead would flash the rail
    // inside the page and then teleport it, which reads as a glitch.
    return null;
  }
  return createPortal(rail, chrome.subrailHost);
}
